import { useState, useEffect, useRef } from 'react';
import { X, Brain, Users, UserCheck, UserPlus, Trash2, Check, AlertCircle, Pin, ChevronDown, ChevronRight, RotateCcw, FileText, Eye } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ref as fbStorageRef, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import { T } from '../theme';
import { subscribeToMemory, deleteMemoryFact, clearAllMemory, pinMemoryFact, restoreArchivedFact, compactMemory } from '../utils/bookkeeperApi';
import {
  subscribeToAdvisors, subscribeToClients, subscribeToAdvisorRequests, subscribeToPendingFacts,
  sendAdvisorInvite, acceptAdvisorRequest, revokeAdvisor, suggestFact,
  approvePendingFact, rejectPendingFact, toggleMemoryFact,
  subscribeToSuggestionLibrary, addToSuggestionLibrary, updateSuggestionLibraryItem,
  deleteFromSuggestionLibrary, subscribeToSentSuggestions, retractSuggestion,
} from '../utils/advisorStorage';
import {
  subscribeToContextDocs, subscribeToPendingDocs, subscribeToSentDocSuggestions,
  ingestContextDoc, deleteContextDoc, approveContextDoc, rejectPendingDoc,
  sendContextDoc, retractContextDoc, uploadContextImage,
} from '../utils/contextDocsStorage';

let _pdfjs = null;
async function getPdfjs() {
  if (!_pdfjs) {
    _pdfjs = await import('pdfjs-dist');
    _pdfjs.GlobalWorkerOptions.workerSrc = new URL(
      'pdfjs-dist/build/pdf.worker.min.mjs',
      import.meta.url,
    ).href;
  }
  return _pdfjs;
}

async function extractPdfText(file) {
  const pdfjsLib = await getPdfjs();
  const buffer   = await file.arrayBuffer();
  const pdf      = await pdfjsLib.getDocument({ data: new Uint8Array(buffer) }).promise;
  let text       = '';
  for (let i = 1; i <= pdf.numPages; i++) {
    const page    = await pdf.getPage(i);
    const content = await page.getTextContent();
    text += content.items.map(it => it.str).join(' ') + '\n';
  }
  return text.trim();
}

const TYPE_BADGE = { text: 'TXT', markdown: 'MD', pdf: 'PDF', image: 'IMG' };

function relativeDate(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return mins <= 1 ? 'just now' : `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

// ── Memory tab ────────────────────────────────────────────────────────────────

function MemoryTab({ uid }) {
  const [facts, setFacts]               = useState([]);
  const [pendingFacts, setPendingFacts] = useState([]);
  const [confirmClear, setConfirmClear] = useState(false);
  const [deleting, setDeleting]         = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [approving, setApproving]       = useState(null);
  const [toggling, setToggling]         = useState(null);
  const [pinning, setPinning]           = useState(null);
  const [restoring, setRestoring]       = useState(null);
  const [compacting, setCompacting]     = useState(false);
  const [compactToast, setCompactToast] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    if (!uid) return;
    const u1 = subscribeToMemory(uid, setFacts);
    const u2 = subscribeToPendingFacts(uid, setPendingFacts);
    return () => { u1(); u2(); };
  }, [uid]);

  const activeFacts   = facts.filter(f => (f.status ?? 'active') !== 'archived');
  const archivedFacts = facts.filter(f => f.status === 'archived');
  const activeUserCount = activeFacts.filter(f =>
    (f.status ?? 'active') === 'active' && !f.pinned && f.source?.type === 'user'
  ).length;
  const dimmedCount   = activeFacts.filter(f => f.status === 'dimmed').length;

  async function handleDelete(fact) {
    if (confirmDelete?.id !== fact.id) { setConfirmDelete(fact); return; }
    setConfirmDelete(null);
    setDeleting(fact.id);
    try { await deleteMemoryFact(uid, fact); } finally { setDeleting(null); }
  }

  async function handleApprove(pf) {
    setApproving(pf.id);
    try { await approvePendingFact(uid, pf); } finally { setApproving(null); }
  }

  async function handleReject(pf) {
    setApproving(pf.id);
    try { await rejectPendingFact(uid, pf.id); } finally { setApproving(null); }
  }

  async function handleToggle(fact) {
    setToggling(fact.id);
    try { await toggleMemoryFact(uid, fact.id); } finally { setToggling(null); }
  }

  async function handlePin(fact) {
    setPinning(fact.id);
    try { await pinMemoryFact(uid, fact.id, !fact.pinned); } finally { setPinning(null); }
  }

  async function handleRestore(fact) {
    setRestoring(fact.id);
    try { await restoreArchivedFact(uid, fact.id); } finally { setRestoring(null); }
  }

  async function handleCompact() {
    setCompacting(true);
    const before = activeUserCount;
    try {
      const { removedCount, addedCount } = await compactMemory();
      const after = before - removedCount + addedCount;
      setCompactToast(`Compacted: ${before} → ${after} facts`);
      setTimeout(() => setCompactToast(null), 4000);
    } catch (e) {
      setCompactToast('Compaction failed');
      setTimeout(() => setCompactToast(null), 3000);
    } finally {
      setCompacting(false);
    }
  }

  function FactRow({ fact }) {
    const isEnabled = fact.enabled !== false;
    const isDimmed  = fact.status === 'dimmed';
    const isPinned  = fact.pinned === true;
    return (
      <div style={{ background: '#fff', borderRadius: 12, padding: '12px 14px', border: '1.5px solid #E5E7EB', display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8, opacity: (isEnabled && !isDimmed) ? 1 : 0.6 }}>
        <button
          onClick={() => handlePin(fact)}
          disabled={pinning === fact.id}
          title={isPinned ? 'Unpin (fact will age normally)' : 'Pin (never ages out)'}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 2px 0', flexShrink: 0, color: isPinned ? T.amberDeep : '#D1D5DB', display: 'flex' }}
        >
          <Pin size={13} fill={isPinned ? T.amberDeep : 'none'} />
        </button>
        <input
          type="checkbox"
          checked={isEnabled}
          disabled={toggling === fact.id}
          onChange={() => handleToggle(fact)}
          style={{ marginTop: 3, accentColor: T.tealDeep, cursor: 'pointer', flexShrink: 0 }}
          title={isEnabled ? 'Disable this memory' : 'Enable this memory'}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', marginBottom: 3 }}>
            {fact.source?.type === 'advisor' && (
              <span style={{ fontSize: 10, fontWeight: 700, color: T.tealDeep, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                from {fact.source.name}
              </span>
            )}
            {isPinned && (
              <span style={{ fontSize: 10, fontWeight: 700, color: T.amberDeep, background: '#FEF3C7', borderRadius: 4, padding: '1px 5px' }}>pinned</span>
            )}
            {isDimmed && !isPinned && (
              <span style={{ fontSize: 10, fontWeight: 600, color: '#6B7280', background: '#F3F4F6', borderRadius: 4, padding: '1px 5px' }}>dimmed</span>
            )}
          </div>
          <div style={{ fontSize: 13, color: '#1F2937', lineHeight: 1.5, textDecoration: isEnabled ? 'none' : 'line-through' }}>{fact.text}</div>
          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>{relativeDate(fact.createdAt)}</div>
        </div>
        {confirmDelete?.id === fact.id ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            <button
              onClick={() => handleDelete(fact)}
              disabled={deleting === fact.id}
              style={{ fontSize: 11, fontWeight: 700, color: '#EF4444', background: 'none', border: '1px solid #FCA5A5', borderRadius: 5, padding: '2px 7px', cursor: 'pointer' }}
            >
              Delete
            </button>
            <button
              onClick={() => setConfirmDelete(null)}
              style={{ fontSize: 11, color: '#9CA3AF', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px' }}
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={() => handleDelete(fact)}
            disabled={deleting === fact.id}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, flexShrink: 0, color: deleting === fact.id ? '#D1D5DB' : '#9CA3AF', display: 'flex', borderRadius: 6 }}
            title="Forget this"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Compact toast */}
      {compactToast && (
        <div style={{ background: T.tealDeep, color: '#fff', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, textAlign: 'center' }}>
          {compactToast}
        </div>
      )}

      {/* Pending from advisors */}
      {pendingFacts.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.tealDeep, textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 8 }}>
            Pending from advisors
          </div>
          {pendingFacts.map(pf => (
            <div key={pf.id} style={{ background: T.tealPale, borderRadius: 12, padding: '12px 14px', border: `1.5px solid ${T.tealBright}40`, marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.tealDeep, marginBottom: 4 }}>{pf.fromAdvisorName}</div>
              <div style={{ fontSize: 13, color: '#1F2937', lineHeight: 1.5, marginBottom: 10 }}>"{pf.text}"</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => handleApprove(pf)}
                  disabled={approving === pf.id}
                  style={{ flex: 1, fontSize: 12, fontWeight: 700, background: T.tealDeep, color: '#fff', border: 'none', borderRadius: 8, padding: '6px 0', cursor: 'pointer' }}
                >
                  Approve
                </button>
                <button
                  onClick={() => handleReject(pf)}
                  disabled={approving === pf.id}
                  style={{ flex: 1, fontSize: 12, fontWeight: 600, background: '#fff', color: '#6B7280', border: '1px solid #E5E7EB', borderRadius: 8, padding: '6px 0', cursor: 'pointer' }}
                >
                  Reject
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {facts.length === 0 && pendingFacts.length === 0 ? (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '32px 16px', textAlign: 'center' }}>
          <div style={{ background: T.tealPale, borderRadius: '50%', padding: 14 }}>
            <Brain size={26} color={T.tealDeep} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.navy }}>No memories yet</div>
          <div style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.6, maxWidth: 260 }}>
            Tell me "always classify Amazon as business" or "remember my home office deduction is 30%" and I'll keep them across sessions.
          </div>
        </div>
      ) : activeFacts.length > 0 && (
        <div>
          {/* Status summary + Compact button */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: '#6B7280' }}>
              {activeUserCount} active{dimmedCount > 0 ? ` · ${dimmedCount} dimmed` : ''}{archivedFacts.length > 0 ? ` · ${archivedFacts.length} archived` : ''}
            </div>
            {activeUserCount >= 5 && (
              <button
                onClick={handleCompact}
                disabled={compacting}
                style={{ fontSize: 11, fontWeight: 700, color: T.tealDeep, background: T.tealPale, border: `1px solid ${T.tealBright}60`, borderRadius: 6, padding: '3px 10px', cursor: compacting ? 'default' : 'pointer', opacity: compacting ? 0.6 : 1 }}
              >
                {compacting ? 'Compacting…' : 'Compact'}
              </button>
            )}
          </div>

          {(activeFacts.length > 0 || pendingFacts.length > 0) && pendingFacts.length > 0 && (
            <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 8 }}>
              Stored
            </div>
          )}
          {activeFacts.map(fact => <FactRow key={fact.id} fact={fact} />)}
        </div>
      )}

      {/* Archived section */}
      {archivedFacts.length > 0 && (
        <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: 8 }}>
          <button
            onClick={() => setShowArchived(v => !v)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 12, fontWeight: 600, color: '#9CA3AF' }}
          >
            {showArchived ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            Archived ({archivedFacts.length})
          </button>
          {showArchived && (
            <div style={{ marginTop: 8 }}>
              {archivedFacts.map(fact => (
                <div key={fact.id} style={{ background: '#F9FAFB', borderRadius: 12, padding: '10px 14px', border: '1.5px solid #E5E7EB', display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8, opacity: 0.7 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {fact.source?.type === 'advisor' && (
                      <div style={{ fontSize: 10, fontWeight: 700, color: T.tealDeep, marginBottom: 3 }}>from {fact.source.name}</div>
                    )}
                    <div style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.5 }}>{fact.text}</div>
                    <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>{relativeDate(fact.createdAt)}</div>
                  </div>
                  <button
                    onClick={() => handleRestore(fact)}
                    disabled={restoring === fact.id}
                    title="Restore to active"
                    style={{ background: 'none', border: '1px solid #E5E7EB', borderRadius: 6, cursor: 'pointer', padding: '3px 6px', flexShrink: 0, color: '#6B7280', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}
                  >
                    <RotateCcw size={11} /> Restore
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Clear all */}
      {facts.length > 0 && (
        <div style={{ marginTop: 'auto', paddingTop: 8, borderTop: '1px solid #E5E7EB' }}>
          {confirmClear ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: '#6B7280', flex: 1 }}>Clear all {facts.length} memories?</span>
              <button onClick={async () => { await clearAllMemory(uid); setConfirmClear(false); }} style={{ fontSize: 12, fontWeight: 700, color: '#EF4444', background: 'none', border: '1px solid #FCA5A5', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}>
                Clear all
              </button>
              <button onClick={() => setConfirmClear(false)} style={{ fontSize: 12, color: '#6B7280', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px' }}>
                Cancel
              </button>
            </div>
          ) : (
            <button onClick={() => setConfirmClear(true)} style={{ fontSize: 12, color: '#9CA3AF', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              Clear all memories
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Advisors tab ──────────────────────────────────────────────────────────────

function AdvisorsTab({ uid }) {
  const [advisors, setAdvisors]   = useState([]);
  const [requests, setRequests]   = useState([]);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting]   = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [revoking, setRevoking]       = useState(null);
  const [confirmRevoke, setConfirmRevoke] = useState(null);
  const [accepting, setAccepting]     = useState(null);

  useEffect(() => {
    if (!uid) return;
    const u1 = subscribeToAdvisors(uid, setAdvisors);
    const u2 = subscribeToAdvisorRequests(uid, setRequests);
    return () => { u1(); u2(); };
  }, [uid]);

  const activeAdvisors = advisors.filter(a => a.status === 'active');

  async function handleInvite() {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setInviteError(null);
    try {
      await sendAdvisorInvite({ targetEmail: inviteEmail.trim(), permissions: { canSuggestFacts: true, canSuggestPrompts: false } });
      setInviteSuccess(true);
      setInviteEmail('');
      setTimeout(() => { setInviteSuccess(false); setShowInvite(false); }, 2000);
    } catch (err) {
      setInviteError(err.message || 'Failed to send invite.');
    } finally {
      setInviting(false);
    }
  }

  async function handleAccept(req) {
    setAccepting(req.id);
    try { await acceptAdvisorRequest(req.id); } catch { /* ignore */ } finally { setAccepting(null); }
  }

  async function handleRevoke(advisorUid) {
    if (confirmRevoke !== advisorUid) { setConfirmRevoke(advisorUid); return; }
    setConfirmRevoke(null);
    setRevoking(advisorUid);
    try { await revokeAdvisor(advisorUid); } catch { /* ignore */ } finally { setRevoking(null); }
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Pending requests */}
      {requests.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.amberDeep, textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 8 }}>
            Pending requests
          </div>
          {requests.map(req => (
            <div key={req.id} style={{ background: T.amberPale, borderRadius: 12, padding: '12px 14px', border: `1.5px solid ${T.amberBright}60`, marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: T.navy }}>{req.fromName || req.fromEmail}</div>
              <div style={{ fontSize: 12, color: '#6B7280', marginBottom: req.message ? 6 : 10 }}>{req.fromEmail}</div>
              {req.message && <div style={{ fontSize: 12, color: '#4B5563', fontStyle: 'italic', marginBottom: 10 }}>"{req.message}"</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => handleAccept(req)}
                  disabled={accepting === req.id}
                  style={{ flex: 1, fontSize: 12, fontWeight: 700, background: T.gradTeal, color: '#fff', border: 'none', borderRadius: 8, padding: '6px 0', cursor: 'pointer' }}
                >
                  Accept
                </button>
                <button
                  onClick={() => handleRevoke(req.fromUid)}
                  disabled={accepting === req.id}
                  style={{ flex: 1, fontSize: 12, color: '#6B7280', background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, padding: '6px 0', cursor: 'pointer' }}
                >
                  Decline
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Active advisors */}
      {activeAdvisors.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 8 }}>
            My advisors
          </div>
          {activeAdvisors.map(a => (
            <div key={a.id} style={{ background: '#fff', borderRadius: 12, padding: '12px 14px', border: '1.5px solid #E5E7EB', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.navy, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.advisorName || a.advisorEmail}</div>
                <div style={{ fontSize: 12, color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.advisorEmail}</div>
              </div>
              {confirmRevoke === a.advisorUid ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                  <button
                    onClick={() => handleRevoke(a.advisorUid)}
                    disabled={revoking === a.advisorUid}
                    style={{ fontSize: 11, fontWeight: 700, color: '#EF4444', background: 'none', border: '1px solid #FCA5A5', borderRadius: 5, padding: '2px 7px', cursor: 'pointer' }}
                  >
                    Revoke
                  </button>
                  <button
                    onClick={() => setConfirmRevoke(null)}
                    style={{ fontSize: 11, color: '#9CA3AF', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px' }}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => handleRevoke(a.advisorUid)}
                  disabled={revoking === a.advisorUid}
                  style={{ fontSize: 11, color: '#9CA3AF', background: 'none', border: '1px solid #E5E7EB', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', flexShrink: 0 }}
                >
                  Revoke
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {activeAdvisors.length === 0 && requests.length === 0 && !showInvite && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '32px 16px', textAlign: 'center' }}>
          <div style={{ background: T.tealPale, borderRadius: '50%', padding: 14 }}>
            <UserCheck size={26} color={T.tealDeep} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.navy }}>No advisors yet</div>
          <div style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.6, maxWidth: 260 }}>
            Invite a trusted person — an accountant, bookkeeper, or colleague — to suggest standing instructions for your AI.
          </div>
        </div>
      )}

      {/* Invite form */}
      {showInvite ? (
        <div style={{ background: '#fff', borderRadius: 12, padding: 16, border: '1.5px solid #E5E7EB' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: T.navy, marginBottom: 10 }}>Invite an advisor</div>
          <input
            type="email"
            placeholder="advisor@example.com"
            value={inviteEmail}
            onChange={e => setInviteEmail(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleInvite()}
            style={{ width: '100%', fontSize: 13, padding: '8px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8, outline: 'none', boxSizing: 'border-box', marginBottom: 8 }}
            autoFocus
          />
          {inviteError && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#EF4444', marginBottom: 8 }}>
              <AlertCircle size={13} /> {inviteError}
            </div>
          )}
          {inviteSuccess && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: T.tealDeep, marginBottom: 8 }}>
              <Check size={13} /> Invite sent!
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleInvite}
              disabled={inviting || !inviteEmail.trim()}
              style={{ flex: 1, fontSize: 13, fontWeight: 700, background: T.gradTeal, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 0', cursor: inviting ? 'default' : 'pointer', opacity: inviting ? 0.7 : 1 }}
            >
              {inviting ? 'Sending…' : 'Send invite'}
            </button>
            <button
              onClick={() => { setShowInvite(false); setInviteEmail(''); setInviteError(null); }}
              style={{ fontSize: 13, color: '#6B7280', background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, padding: '8px 14px', cursor: 'pointer' }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowInvite(true)}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', fontSize: 13, fontWeight: 700, color: T.tealDeep, background: T.tealPale, border: `1.5px dashed ${T.tealBright}`, borderRadius: 10, padding: '10px 0', cursor: 'pointer' }}
        >
          <UserPlus size={15} /> Invite advisor
        </button>
      )}
    </div>
  );
}

// ── Clients tab ───────────────────────────────────────────────────────────────

function ClientsTab({ uid }) {
  const [clients, setClients]               = useState([]);
  const [library, setLibrary]               = useState([]);
  const [sent, setSent]                     = useState([]);

  const [editingItemId, setEditingItemId]   = useState(null);
  const [editText, setEditText]             = useState('');
  const [savingItem, setSavingItem]         = useState(false);
  const [itemError, setItemError]           = useState(null);

  const [sendPanel, setSendPanel]           = useState(null);
  const [sendText, setSendText]             = useState('');
  const [selectedClients, setSelectedClients] = useState({});
  const [sending, setSending]               = useState(false);
  const [sendResults, setSendResults]       = useState(null);

  const [retracting, setRetracting]         = useState(null);
  const [confirmRetract, setConfirmRetract] = useState(null);
  const [confirmDeleteLib, setConfirmDeleteLib] = useState(null);
  const [showLibrary, setShowLibrary]       = useState(false);

  // Document-sending state
  const [sentDocs, setSentDocs]             = useState([]);
  const [showDocPanel, setShowDocPanel]     = useState(false);
  const [docForm, setDocForm]               = useState({ title: '', contentType: 'text', text: '' });
  const [selectedDocClients, setSelectedDocClients] = useState({});
  const [sendingDoc, setSendingDoc]         = useState(false);
  const [docSendResults, setDocSendResults] = useState(null);
  const [retractingDoc, setRetractingDoc]   = useState(null);
  const [confirmRetractDoc, setConfirmRetractDoc] = useState(null);

  useEffect(() => {
    if (!uid) return;
    const u1 = subscribeToClients(uid, setClients);
    const u2 = subscribeToSuggestionLibrary(uid, setLibrary);
    const u3 = subscribeToSentSuggestions(uid, setSent);
    const u4 = subscribeToSentDocSuggestions(uid, setSentDocs);
    return () => { u1(); u2(); u3(); u4(); };
  }, [uid]);

  const activeClients = clients.filter(c => c.status === 'active');

  async function handleSaveLibraryItem() {
    if (!editText.trim()) return;
    setSavingItem(true);
    setItemError(null);
    try {
      if (editingItemId === 'new') {
        await addToSuggestionLibrary(uid, editText.trim());
      } else {
        await updateSuggestionLibraryItem(uid, editingItemId, editText.trim());
      }
      setEditingItemId(null);
      setEditText('');
    } catch (err) {
      setItemError(err.message || 'Failed to save.');
    } finally {
      setSavingItem(false);
    }
  }

  function openSendPanel(libraryItem = null) {
    setSendPanel({ libraryItemId: libraryItem?.id ?? null });
    setSendText(libraryItem?.text ?? '');
    setSelectedClients({});
    setSendResults(null);
  }

  async function handleSend() {
    const targets = activeClients.filter(c => selectedClients[c.clientUid]);
    if (!targets.length || !sendText.trim()) return;
    setSending(true);
    setSendResults(null);
    const results = await Promise.allSettled(
      targets.map(c => suggestFact(c.clientUid, sendText.trim(), sendPanel?.libraryItemId ?? null))
    );
    const ok   = targets.filter((_, i) => results[i].status === 'fulfilled').map(c => c.clientName || c.clientEmail);
    const fail = targets
      .map((c, i) => results[i].status === 'rejected' ? { name: c.clientName || c.clientEmail, reason: results[i].reason?.message || 'Failed' } : null)
      .filter(Boolean);
    setSendResults({ ok, fail });
    setSending(false);
    if (!fail.length) {
      setTimeout(() => { setSendPanel(null); setSendText(''); setSelectedClients({}); setSendResults(null); }, 1800);
    }
  }

  async function handleDeleteLib(item) {
    if (confirmDeleteLib !== item.id) { setConfirmDeleteLib(item.id); return; }
    setConfirmDeleteLib(null);
    await deleteFromSuggestionLibrary(uid, item.id);
  }

  async function handleRetract(s) {
    if (confirmRetract !== s.id) { setConfirmRetract(s.id); return; }
    setConfirmRetract(null);
    setRetracting(s.id);
    try { await retractSuggestion(s.clientUid, s.pendingFactId, s.id); } catch { /* ignore */ } finally { setRetracting(null); }
  }

  async function handleSendDoc() {
    const targets = activeClients.filter(c => selectedDocClients[c.clientUid]);
    if (!targets.length || !docForm.title.trim() || !docForm.text.trim()) return;
    setSendingDoc(true);
    setDocSendResults(null);
    const results = await Promise.allSettled(
      targets.map(c => sendContextDoc(c.clientUid, { title: docForm.title.trim(), contentType: docForm.contentType, text: docForm.text.trim() }))
    );
    const ok   = targets.filter((_, i) => results[i].status === 'fulfilled').map(c => c.clientName || c.clientEmail);
    const fail = targets
      .map((c, i) => results[i].status === 'rejected' ? { name: c.clientName || c.clientEmail, reason: results[i].reason?.message || 'Failed' } : null)
      .filter(Boolean);
    setDocSendResults({ ok, fail });
    setSendingDoc(false);
    if (!fail.length) {
      setTimeout(() => { setShowDocPanel(false); setDocForm({ title: '', contentType: 'text', text: '' }); setSelectedDocClients({}); setDocSendResults(null); }, 1800);
    }
  }

  async function handleRetractDoc(s) {
    if (confirmRetractDoc !== s.id) { setConfirmRetractDoc(s.id); return; }
    setConfirmRetractDoc(null);
    setRetractingDoc(s.id);
    try { await retractContextDoc(s.clientUid, s.pendingDocId, s.id); } catch { /* ignore */ } finally { setRetractingDoc(null); }
  }

  if (activeClients.length === 0 && library.length === 0 && sent.length === 0 && sentDocs.length === 0) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '32px 16px', textAlign: 'center' }}>
        <div style={{ background: T.tealPale, borderRadius: '50%', padding: 14 }}>
          <Users size={26} color={T.tealDeep} />
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: T.navy }}>No clients yet</div>
        <div style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.6, maxWidth: 260 }}>
          When someone adds you as an advisor, they'll appear here.
        </div>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── My Suggestions Library ──────────────────────────────────── */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: showLibrary ? 8 : 0 }}>
          <button
            onClick={() => setShowLibrary(v => !v)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.7 }}
          >
            {showLibrary ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            My Suggestions Library
          </button>
          {showLibrary && (
            <button
              onClick={() => { setEditingItemId('new'); setEditText(''); setItemError(null); }}
              style={{ fontSize: 11, fontWeight: 700, color: T.tealDeep, background: 'none', border: 'none', cursor: 'pointer' }}
            >
              + Add
            </button>
          )}
        </div>

        {showLibrary && editingItemId && (
          <div style={{ background: T.tealPale, borderRadius: 10, padding: 12, border: `1.5px solid ${T.tealBright}40`, marginBottom: 8 }}>
            <textarea
              value={editText}
              onChange={e => setEditText(e.target.value.slice(0, 128))}
              maxLength={128}
              rows={2}
              placeholder='e.g. "Home office deduction is 30%"'
              style={{ width: '100%', fontSize: 13, padding: '8px 10px', border: '1.5px solid #E5E7EB', borderRadius: 8, outline: 'none', resize: 'none', boxSizing: 'border-box', fontFamily: 'inherit', marginBottom: 4 }}
              autoFocus
            />
            <div style={{ fontSize: 11, color: '#9CA3AF', textAlign: 'right', marginBottom: 6 }}>{editText.length}/128</div>
            {itemError && <div style={{ fontSize: 12, color: '#EF4444', marginBottom: 6 }}>{itemError}</div>}
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={handleSaveLibraryItem}
                disabled={savingItem || !editText.trim()}
                style={{ flex: 1, fontSize: 12, fontWeight: 700, background: T.gradTeal, color: '#fff', border: 'none', borderRadius: 8, padding: '6px 0', cursor: 'pointer', opacity: savingItem ? 0.7 : 1 }}
              >
                {savingItem ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={() => { setEditingItemId(null); setEditText(''); setItemError(null); }}
                style={{ fontSize: 12, color: '#6B7280', background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, padding: '6px 12px', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {showLibrary && library.length === 0 && !editingItemId && (
          <div style={{ fontSize: 12, color: '#9CA3AF', textAlign: 'center', padding: '8px 0 4px' }}>
            No saved suggestions yet.
          </div>
        )}

        {showLibrary && library.map(item => (
          <div key={item.id} style={{ background: '#fff', borderRadius: 10, padding: '10px 12px', border: '1.5px solid #E5E7EB', display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6 }}>
            <div style={{ flex: 1, fontSize: 13, color: '#1F2937', lineHeight: 1.4 }}>{item.text}</div>
            <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
              <button
                onClick={() => openSendPanel(item)}
                style={{ fontSize: 11, fontWeight: 700, color: T.tealDeep, background: T.tealPale, border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer' }}
              >
                Send
              </button>
              <button
                onClick={() => { setEditingItemId(item.id); setEditText(item.text); setItemError(null); }}
                style={{ fontSize: 11, color: '#6B7280', background: 'none', border: '1px solid #E5E7EB', borderRadius: 6, padding: '4px 8px', cursor: 'pointer' }}
              >
                Edit
              </button>
              {confirmDeleteLib === item.id ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <button
                    onClick={() => handleDeleteLib(item)}
                    style={{ fontSize: 11, fontWeight: 700, color: '#EF4444', background: 'none', border: '1px solid #FCA5A5', borderRadius: 5, padding: '2px 7px', cursor: 'pointer' }}
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => setConfirmDeleteLib(null)}
                    style={{ fontSize: 11, color: '#9CA3AF', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px' }}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => handleDeleteLib(item)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', color: '#9CA3AF', display: 'flex', borderRadius: 6 }}
                  title="Delete"
                >
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          </div>
        ))}

      </div>

      <button
        onClick={() => openSendPanel(null)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', fontSize: 13, fontWeight: 700, color: T.tealDeep, background: T.tealPale, border: `1.5px dashed ${T.tealBright}`, borderRadius: 10, padding: '10px 0', cursor: 'pointer' }}
      >
        + New suggestion
      </button>

      {/* ── Send Panel ──────────────────────────────────────────────── */}
      {sendPanel && (
        <div style={{ background: '#fff', borderRadius: 12, padding: 14, border: `1.5px solid ${T.tealBright}60` }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.navy, marginBottom: 8 }}>Send suggestion</div>
          <textarea
            value={sendText}
            onChange={e => setSendText(e.target.value.slice(0, 128))}
            maxLength={128}
            rows={2}
            placeholder='Suggestion text (max 128 chars)'
            style={{ width: '100%', fontSize: 13, padding: '8px 10px', border: '1.5px solid #E5E7EB', borderRadius: 8, outline: 'none', resize: 'none', boxSizing: 'border-box', fontFamily: 'inherit', marginBottom: 4 }}
          />
          <div style={{ fontSize: 11, color: '#9CA3AF', textAlign: 'right', marginBottom: 10 }}>{sendText.length}/128</div>

          <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 6 }}>Send to:</div>
          {activeClients.length === 0 && (
            <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 8 }}>No active clients.</div>
          )}
          {activeClients.map(c => (
            <label key={c.clientUid} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', cursor: 'pointer', fontSize: 13, color: '#1F2937' }}>
              <input
                type="checkbox"
                checked={!!selectedClients[c.clientUid]}
                onChange={e => setSelectedClients(prev => ({ ...prev, [c.clientUid]: e.target.checked }))}
                style={{ accentColor: T.tealDeep }}
              />
              <span style={{ fontWeight: 600 }}>{c.clientName || c.clientEmail}</span>
              {c.clientName && <span style={{ fontSize: 11, color: '#9CA3AF' }}>{c.clientEmail}</span>}
            </label>
          ))}

          {sendResults && (
            <div style={{ marginTop: 8, fontSize: 12 }}>
              {sendResults.ok.map(name => (
                <div key={name} style={{ color: T.tealDeep, display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                  <Check size={12} /> {name}: sent
                </div>
              ))}
              {sendResults.fail.map(({ name, reason }) => (
                <div key={name} style={{ color: '#EF4444', marginBottom: 2 }}>{name}: {reason}</div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button
              onClick={handleSend}
              disabled={sending || !sendText.trim() || !activeClients.some(c => selectedClients[c.clientUid])}
              style={{ flex: 1, fontSize: 12, fontWeight: 700, background: T.gradTeal, color: '#fff', border: 'none', borderRadius: 8, padding: '7px 0', cursor: 'pointer', opacity: sending ? 0.7 : 1 }}
            >
              {sending ? 'Sending…' : `Send to ${activeClients.filter(c => selectedClients[c.clientUid]).length} client(s)`}
            </button>
            <button
              onClick={() => { setSendPanel(null); setSendText(''); setSelectedClients({}); setSendResults(null); }}
              style={{ fontSize: 12, color: '#6B7280', background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, padding: '7px 12px', cursor: 'pointer' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Send Document button ────────────────────────────────────── */}
      <button
        onClick={() => { setShowDocPanel(v => !v); setDocSendResults(null); }}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', fontSize: 13, fontWeight: 700, color: T.tealDeep, background: T.tealPale, border: `1.5px dashed ${T.tealBright}`, borderRadius: 10, padding: '10px 0', cursor: 'pointer' }}
      >
        + Send Document
      </button>

      {/* ── Send Document Panel ─────────────────────────────────────── */}
      {showDocPanel && (
        <div style={{ background: '#fff', borderRadius: 12, padding: 14, border: `1.5px solid ${T.tealBright}60` }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.navy, marginBottom: 10 }}>Send context document</div>

          <input
            type="text"
            value={docForm.title}
            onChange={e => setDocForm(f => ({ ...f, title: e.target.value.slice(0, 100) }))}
            placeholder='Document title (e.g. "Client Expense Policy")'
            style={{ width: '100%', fontSize: 13, padding: '8px 10px', border: '1.5px solid #E5E7EB', borderRadius: 8, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', marginBottom: 8 }}
          />

          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            {['text', 'markdown'].map(t => (
              <button
                key={t}
                onClick={() => setDocForm(f => ({ ...f, contentType: t }))}
                style={{ flex: 1, fontSize: 11, fontWeight: 700, padding: '5px 0', borderRadius: 6, cursor: 'pointer', border: `1.5px solid ${docForm.contentType === t ? T.tealDeep : '#E5E7EB'}`, background: docForm.contentType === t ? T.tealPale : '#fff', color: docForm.contentType === t ? T.tealDeep : '#6B7280' }}
              >
                {TYPE_BADGE[t]}
              </button>
            ))}
          </div>

          <textarea
            value={docForm.text}
            onChange={e => setDocForm(f => ({ ...f, text: e.target.value.slice(0, 5000) }))}
            rows={5}
            placeholder='Document content (max 5,000 chars)'
            style={{ width: '100%', fontSize: 13, padding: '8px 10px', border: '1.5px solid #E5E7EB', borderRadius: 8, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', marginBottom: 4 }}
          />
          <div style={{ fontSize: 11, color: '#9CA3AF', textAlign: 'right', marginBottom: 10 }}>{docForm.text.length}/5000</div>

          <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 6 }}>Send to:</div>
          {activeClients.map(c => (
            <label key={c.clientUid} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', cursor: 'pointer', fontSize: 13, color: '#1F2937' }}>
              <input
                type="checkbox"
                checked={!!selectedDocClients[c.clientUid]}
                onChange={e => setSelectedDocClients(prev => ({ ...prev, [c.clientUid]: e.target.checked }))}
                style={{ accentColor: T.tealDeep }}
              />
              <span style={{ fontWeight: 600 }}>{c.clientName || c.clientEmail}</span>
            </label>
          ))}

          {docSendResults && (
            <div style={{ marginTop: 8, fontSize: 12 }}>
              {docSendResults.ok.map(name => (
                <div key={name} style={{ color: T.tealDeep, display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                  <Check size={12} /> {name}: sent
                </div>
              ))}
              {docSendResults.fail.map(({ name, reason }) => (
                <div key={name} style={{ color: '#EF4444', marginBottom: 2 }}>{name}: {reason}</div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button
              onClick={handleSendDoc}
              disabled={sendingDoc || !docForm.title.trim() || !docForm.text.trim() || !activeClients.some(c => selectedDocClients[c.clientUid])}
              style={{ flex: 1, fontSize: 12, fontWeight: 700, background: T.gradTeal, color: '#fff', border: 'none', borderRadius: 8, padding: '7px 0', cursor: 'pointer', opacity: sendingDoc ? 0.7 : 1 }}
            >
              {sendingDoc ? 'Sending…' : `Send to ${activeClients.filter(c => selectedDocClients[c.clientUid]).length} client(s)`}
            </button>
            <button
              onClick={() => { setShowDocPanel(false); setDocForm({ title: '', contentType: 'text', text: '' }); setSelectedDocClients({}); setDocSendResults(null); }}
              style={{ fontSize: 12, color: '#6B7280', background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, padding: '7px 12px', cursor: 'pointer' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ── Sent Documents History ──────────────────────────────────── */}
      {sentDocs.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 8 }}>
            Sent Documents
          </div>
          {sentDocs.map(s => (
            <div key={s.id} style={{ background: '#fff', borderRadius: 10, padding: '10px 12px', border: '1.5px solid #E5E7EB', marginBottom: 6 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: T.navy, marginBottom: 1 }}>{s.title}</div>
                  <div style={{ fontSize: 12, color: '#4B5563' }}>{s.clientName || s.clientUid}</div>
                  <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
                    {relativeDate(s.sentAt)}
                    {s.status === 'retracted' && <span style={{ color: '#EF4444', marginLeft: 6 }}>· retracted</span>}
                    {s.status === 'approved' && <span style={{ color: T.tealDeep, marginLeft: 6 }}>· approved</span>}
                  </div>
                </div>
                {s.status === 'pending' && (
                  confirmRetractDoc === s.id ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                      <button
                        onClick={() => handleRetractDoc(s)}
                        disabled={retractingDoc === s.id}
                        style={{ fontSize: 11, fontWeight: 700, color: '#EF4444', background: 'none', border: '1px solid #FCA5A5', borderRadius: 5, padding: '2px 7px', cursor: 'pointer' }}
                      >
                        Retract
                      </button>
                      <button
                        onClick={() => setConfirmRetractDoc(null)}
                        style={{ fontSize: 11, color: '#9CA3AF', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px' }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleRetractDoc(s)}
                      disabled={retractingDoc === s.id}
                      style={{ fontSize: 11, color: '#9CA3AF', background: 'none', border: '1px solid #E5E7EB', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', flexShrink: 0, opacity: retractingDoc === s.id ? 0.5 : 1 }}
                    >
                      Retract
                    </button>
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Sent Fact History ────────────────────────────────────────── */}
      {sent.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 8 }}>
            Sent Facts
          </div>
          {sent.map(s => (
            <div key={s.id} style={{ background: '#fff', borderRadius: 10, padding: '10px 12px', border: '1.5px solid #E5E7EB', marginBottom: 6 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: T.navy, marginBottom: 2 }}>{s.clientName || s.clientUid}</div>
                  <div style={{ fontSize: 12, color: '#4B5563', lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.text}</div>
                  <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 3 }}>
                    {relativeDate(s.sentAt)}
                    {s.status === 'retracted' && <span style={{ color: '#EF4444', marginLeft: 6 }}>· retracted</span>}
                  </div>
                </div>
                {s.status === 'pending' && (
                  confirmRetract === s.id ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                      <button
                        onClick={() => handleRetract(s)}
                        disabled={retracting === s.id}
                        style={{ fontSize: 11, fontWeight: 700, color: '#EF4444', background: 'none', border: '1px solid #FCA5A5', borderRadius: 5, padding: '2px 7px', cursor: 'pointer' }}
                      >
                        Retract
                      </button>
                      <button
                        onClick={() => setConfirmRetract(null)}
                        style={{ fontSize: 11, color: '#9CA3AF', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px' }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleRetract(s)}
                      disabled={retracting === s.id}
                      style={{ fontSize: 11, color: '#9CA3AF', background: 'none', border: '1px solid #E5E7EB', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', flexShrink: 0, opacity: retracting === s.id ? 0.5 : 1 }}
                      title="Retract this suggestion"
                    >
                      Retract
                    </button>
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Document viewer modal ─────────────────────────────────────────────────────

function DocViewerModal({ doc, onClose }) {
  const [imageUrl, setImageUrl]   = useState(null);
  const [imgLoading, setImgLoading] = useState(false);
  const [imgError, setImgError]   = useState(null);

  useEffect(() => {
    if (doc.contentType === 'image' && doc.storageRef) {
      setImgLoading(true);
      setImgError(null);
      getDownloadURL(fbStorageRef(storage, doc.storageRef))
        .then(url => setImageUrl(url))
        .catch(() => setImgError('Could not load image.'))
        .finally(() => setImgLoading(false));
    }
  }, [doc]);

  return (
    <div style={{ position: 'absolute', inset: 0, background: T.appBg, zIndex: 20, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ background: T.gradNavy, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 9, fontWeight: 700, background: 'rgba(255,255,255,0.2)', color: '#fff', borderRadius: 4, padding: '2px 6px', letterSpacing: 0.5, flexShrink: 0 }}>
            {TYPE_BADGE[doc.contentType] ?? 'DOC'}
          </span>
          <span style={{ color: '#fff', fontWeight: 700, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.title}</span>
        </div>
        <button
          onClick={onClose}
          style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, padding: '6px 8px', cursor: 'pointer', color: '#fff', display: 'flex', flexShrink: 0 }}
        >
          <X size={18} />
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {(doc.contentType === 'text' || doc.contentType === 'pdf') && (
          <pre style={{ margin: 0, fontFamily: 'inherit', fontSize: 13, color: '#1F2937', whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
            {doc.text || <span style={{ color: '#9CA3AF' }}>No content.</span>}
          </pre>
        )}

        {doc.contentType === 'markdown' && (
          <div style={{ fontSize: 13, color: '#1F2937', lineHeight: 1.7 }}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ children }) => <h1 style={{ fontSize: 18, fontWeight: 800, color: T.navy, margin: '0 0 10px' }}>{children}</h1>,
                h2: ({ children }) => <h2 style={{ fontSize: 15, fontWeight: 700, color: T.navy, margin: '12px 0 6px' }}>{children}</h2>,
                h3: ({ children }) => <h3 style={{ fontSize: 13, fontWeight: 700, color: T.navy, margin: '10px 0 4px' }}>{children}</h3>,
                p: ({ children }) => <p style={{ margin: '0 0 8px' }}>{children}</p>,
                ul: ({ children }) => <ul style={{ margin: '0 0 8px', paddingLeft: 20 }}>{children}</ul>,
                ol: ({ children }) => <ol style={{ margin: '0 0 8px', paddingLeft: 20 }}>{children}</ol>,
                li: ({ children }) => <li style={{ marginBottom: 2 }}>{children}</li>,
                code: ({ inline, children }) => inline
                  ? <code style={{ background: '#F3F4F6', borderRadius: 3, padding: '1px 5px', fontSize: 12, fontFamily: 'monospace' }}>{children}</code>
                  : <pre style={{ background: '#F3F4F6', borderRadius: 6, padding: '10px 12px', fontSize: 12, fontFamily: 'monospace', overflowX: 'auto', margin: '0 0 8px' }}>{children}</pre>,
                blockquote: ({ children }) => <blockquote style={{ borderLeft: `3px solid ${T.tealBright}`, paddingLeft: 12, margin: '0 0 8px', color: '#6B7280' }}>{children}</blockquote>,
                a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: T.tealDeep, textDecoration: 'underline' }}>{children}</a>,
              }}
            >
              {doc.text || ''}
            </ReactMarkdown>
          </div>
        )}

        {doc.contentType === 'image' && (
          <div>
            {imgLoading && (
              <div style={{ textAlign: 'center', padding: 24, color: '#9CA3AF', fontSize: 13 }}>Loading image…</div>
            )}
            {imgError && (
              <div style={{ fontSize: 12, color: '#EF4444', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 12 }}>
                <AlertCircle size={13} /> {imgError}
              </div>
            )}
            {imageUrl && !imgError && (
              <img
                src={imageUrl}
                alt={doc.title}
                style={{ maxWidth: '100%', borderRadius: 8, display: 'block', marginBottom: 16 }}
              />
            )}
            {doc.text && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 6 }}>
                  AI Description
                </div>
                <pre style={{ margin: 0, fontFamily: 'inherit', fontSize: 13, color: '#4B5563', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                  {doc.text}
                </pre>
              </div>
            )}
            {!doc.storageRef && !doc.text && (
              <div style={{ fontSize: 13, color: '#9CA3AF', fontStyle: 'italic' }}>No content available.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Documents tab (Pro only) ──────────────────────────────────────────────────

const BLANK_FORM = { title: '', contentType: 'text', text: '', storageRef: null, imageMimeType: null, imageFile: null };

function DocumentsTab({ uid }) {
  const [docs, setDocs]               = useState([]);
  const [pending, setPending]         = useState([]);
  const [showAdd, setShowAdd]         = useState(false);
  const [form, setForm]               = useState(BLANK_FORM);
  const [extracting, setExtracting]   = useState(false);
  const [ingesting, setIngesting]     = useState(false);
  const [error, setError]             = useState(null);
  const [deleting, setDeleting]       = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [approving, setApproving]     = useState(null);
  const [rejecting, setRejecting]     = useState(null);
  const [viewingDoc, setViewingDoc]   = useState(null);
  const fileInputRef                  = useRef(null);

  useEffect(() => {
    if (!uid) return;
    const u1 = subscribeToContextDocs(uid, setDocs);
    const u2 = subscribeToPendingDocs(uid, setPending);
    return () => { u1(); u2(); };
  }, [uid]);

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    if (form.contentType === 'pdf') {
      setExtracting(true);
      try {
        const text = await extractPdfText(file);
        if (!text) { setError('Could not extract text from this PDF.'); return; }
        setForm(f => ({ ...f, text: text.slice(0, 5000) }));
      } catch (err) {
        setError('PDF extraction failed: ' + err.message);
      } finally {
        setExtracting(false);
        e.target.value = '';
      }
    } else if (form.contentType === 'image') {
      setForm(f => ({ ...f, imageFile: file, imageMimeType: file.type, text: '' }));
    }
  }

  async function handleSubmit() {
    if (!form.title.trim() || ingesting) return;
    setIngesting(true);
    setError(null);
    try {
      let storageRef  = null;
      let imageMimeType = null;
      if (form.contentType === 'image' && form.imageFile) {
        const res = await uploadContextImage(uid, form.imageFile);
        storageRef   = res.storageRef;
        imageMimeType = res.mimeType;
      }
      await ingestContextDoc({
        title:       form.title.trim(),
        contentType: form.contentType,
        text:        form.text,
        storageRef,
        imageMimeType,
      });
      setForm(BLANK_FORM);
      setShowAdd(false);
    } catch (err) {
      setError(err.message || 'Failed to add document.');
    } finally {
      setIngesting(false);
    }
  }

  async function handleDelete(docId) {
    if (confirmDelete !== docId) { setConfirmDelete(docId); return; }
    setConfirmDelete(null);
    setDeleting(docId);
    try { await deleteContextDoc(docId); } catch { /* ignore */ } finally { setDeleting(null); }
  }

  async function handleApprove(p) {
    setApproving(p.id);
    try { await approveContextDoc(p.id); } catch (err) { setError(err.message || 'Approval failed.'); } finally { setApproving(null); }
  }

  async function handleReject(p) {
    setRejecting(p.id);
    try { await rejectPendingDoc(uid, p.id); } catch { /* ignore */ } finally { setRejecting(null); }
  }

  const submitDisabled = ingesting || extracting || !form.title.trim() ||
    (form.contentType !== 'image' && !form.text.trim()) ||
    (form.contentType === 'image' && !form.imageFile);

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>

      {viewingDoc && <DocViewerModal doc={viewingDoc} onClose={() => setViewingDoc(null)} />}

      {/* ── Pending docs from advisor ───────────────────────────────── */}
      {pending.length > 0 && (
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.amberDeep, textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 8 }}>
            From Advisor
          </div>
          {pending.map(p => (
            <div key={p.id} style={{ background: '#FFFBEB', border: `1.5px solid ${T.amberBright}60`, borderRadius: 10, padding: '10px 12px', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                    <span style={{ fontSize: 9, fontWeight: 700, background: T.amberDeep, color: '#fff', borderRadius: 4, padding: '1px 5px', letterSpacing: 0.5 }}>
                      {TYPE_BADGE[p.contentType] ?? 'DOC'}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: T.navy }}>{p.title}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 2 }}>from {p.fromAdvisorName}</div>
                  <div style={{ fontSize: 12, color: '#4B5563', lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {p.text?.slice(0, 120)}{p.text?.length > 120 ? '…' : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
                  <button
                    onClick={() => handleApprove(p)}
                    disabled={approving === p.id}
                    style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: T.gradTeal, border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', opacity: approving === p.id ? 0.7 : 1 }}
                  >
                    {approving === p.id ? '…' : 'Accept'}
                  </button>
                  <button
                    onClick={() => handleReject(p)}
                    disabled={rejecting === p.id}
                    style={{ fontSize: 11, color: '#6B7280', background: 'none', border: '1px solid #E5E7EB', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}
                  >
                    Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── My Documents ────────────────────────────────────────────── */}
      <div>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 8 }}>
          My Documents ({docs.length})
        </div>

        {docs.length === 0 && !showAdd && (
          <div style={{ fontSize: 13, color: '#9CA3AF', textAlign: 'center', padding: '16px 0' }}>
            No context documents yet. Add one below.
          </div>
        )}

        {docs.map(d => (
          <div key={d.id} style={{ background: '#fff', border: '1.5px solid #E5E7EB', borderRadius: 10, padding: '10px 12px', marginBottom: 6, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                <span style={{ fontSize: 9, fontWeight: 700, background: T.tealDeep, color: '#fff', borderRadius: 4, padding: '1px 5px', letterSpacing: 0.5 }}>
                  {TYPE_BADGE[d.contentType] ?? 'DOC'}
                </span>
                <span style={{ fontSize: 13, fontWeight: 700, color: T.navy }}>{d.title}</span>
                {d.source?.type === 'advisor' && (
                  <span style={{ fontSize: 10, color: '#9CA3AF' }}>· from {d.source.name}</span>
                )}
              </div>
              <div style={{ fontSize: 11, color: '#9CA3AF' }}>
                {d.chunkCount} chunk{d.chunkCount !== 1 ? 's' : ''} · {relativeDate(d.createdAt)}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
              <button
                onClick={() => setViewingDoc(d)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', color: '#9CA3AF', display: 'flex', borderRadius: 6 }}
                title="View document"
              >
                <Eye size={13} />
              </button>
              {confirmDelete === d.id ? (
                <>
                  <button
                    onClick={() => handleDelete(d.id)}
                    disabled={deleting === d.id}
                    style={{ fontSize: 11, fontWeight: 700, color: '#EF4444', background: 'none', border: '1px solid #FCA5A5', borderRadius: 5, padding: '2px 7px', cursor: 'pointer' }}
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => setConfirmDelete(null)}
                    style={{ fontSize: 11, color: '#9CA3AF', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px' }}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleDelete(d.id)}
                  disabled={deleting === d.id}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', color: '#9CA3AF', display: 'flex', borderRadius: 6, opacity: deleting === d.id ? 0.4 : 1 }}
                  title="Delete document"
                >
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* ── Add Document ────────────────────────────────────────────── */}
      {!showAdd && (
        <button
          onClick={() => { setShowAdd(true); setError(null); setForm(BLANK_FORM); }}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', fontSize: 13, fontWeight: 700, color: T.tealDeep, background: T.tealPale, border: `1.5px dashed ${T.tealBright}`, borderRadius: 10, padding: '10px 0', cursor: 'pointer' }}
        >
          + Add Document
        </button>
      )}

      {showAdd && (
        <div style={{ background: '#fff', border: `1.5px solid ${T.tealBright}60`, borderRadius: 12, padding: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.navy, marginBottom: 10 }}>Add Context Document</div>

          {/* Title */}
          <div style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.7 }}>Title</label>
              <span style={{ fontSize: 11, color: '#9CA3AF' }}>{form.title.length}/100</span>
            </div>
            <input
              type="text"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value.slice(0, 100) }))}
              placeholder='e.g. "Business Overview" or "Client Expense Policy"'
              style={{ width: '100%', fontSize: 13, padding: '8px 10px', border: '1.5px solid #E5E7EB', borderRadius: 8, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
            />
          </div>

          {/* Content type */}
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 6 }}>Type</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {['text', 'markdown', 'pdf', 'image'].map(t => (
                <button
                  key={t}
                  onClick={() => setForm(f => ({ ...f, contentType: t, text: '', storageRef: null, imageFile: null, imageMimeType: null }))}
                  style={{ flex: 1, fontSize: 11, fontWeight: 700, padding: '5px 0', borderRadius: 6, cursor: 'pointer', border: `1.5px solid ${form.contentType === t ? T.tealDeep : '#E5E7EB'}`, background: form.contentType === t ? T.tealPale : '#fff', color: form.contentType === t ? T.tealDeep : '#6B7280' }}
                >
                  {TYPE_BADGE[t]}
                </button>
              ))}
            </div>
          </div>

          {/* Text/Markdown input */}
          {(form.contentType === 'text' || form.contentType === 'markdown') && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.7 }}>Content</label>
                <span style={{ fontSize: 11, color: form.text.length >= 4800 ? '#EF4444' : '#9CA3AF' }}>{form.text.length}/5000</span>
              </div>
              <textarea
                value={form.text}
                onChange={e => setForm(f => ({ ...f, text: e.target.value.slice(0, 5000) }))}
                rows={6}
                placeholder={form.contentType === 'markdown' ? 'Paste or type Markdown content…' : 'Paste or type your document content…'}
                style={{ width: '100%', fontSize: 13, padding: '8px 10px', border: '1.5px solid #E5E7EB', borderRadius: 8, outline: 'none', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', lineHeight: 1.5 }}
              />
            </div>
          )}

          {/* PDF file picker */}
          {form.contentType === 'pdf' && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 6 }}>PDF File</div>
              <input ref={fileInputRef} type="file" accept=".pdf" onChange={handleFileChange} style={{ display: 'none' }} />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={extracting}
                style={{ width: '100%', fontSize: 13, padding: '8px 0', border: '1.5px dashed #D1D5DB', borderRadius: 8, background: '#F9FAFB', cursor: 'pointer', color: '#4B5563', opacity: extracting ? 0.7 : 1 }}
              >
                {extracting ? 'Extracting text…' : form.text ? '✓ Text extracted — pick another' : 'Choose PDF…'}
              </button>
              {form.text && (
                <div style={{ marginTop: 6, fontSize: 11, color: '#6B7280' }}>
                  {form.text.length} chars extracted
                </div>
              )}
            </div>
          )}

          {/* Image file picker */}
          {form.contentType === 'image' && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 6 }}>Image</div>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />
              <button
                onClick={() => fileInputRef.current?.click()}
                style={{ width: '100%', fontSize: 13, padding: '8px 0', border: '1.5px dashed #D1D5DB', borderRadius: 8, background: '#F9FAFB', cursor: 'pointer', color: '#4B5563' }}
              >
                {form.imageFile ? `✓ ${form.imageFile.name}` : 'Choose image…'}
              </button>
              <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>
                AI will describe the image as the document text.
              </div>
            </div>
          )}

          {error && (
            <div style={{ fontSize: 12, color: '#EF4444', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}>
              <AlertCircle size={13} /> {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={handleSubmit}
              disabled={submitDisabled}
              style={{ flex: 1, fontSize: 12, fontWeight: 700, background: T.gradTeal, color: '#fff', border: 'none', borderRadius: 8, padding: '7px 0', cursor: 'pointer', opacity: submitDisabled ? 0.6 : 1 }}
            >
              {ingesting ? 'Ingesting…' : 'Add Document'}
            </button>
            <button
              onClick={() => { setShowAdd(false); setForm(BLANK_FORM); setError(null); }}
              style={{ fontSize: 12, color: '#6B7280', background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, padding: '7px 12px', cursor: 'pointer' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main modal ────────────────────────────────────────────────────────────────

export default function AISettingsModal({ uid, tier = 'basic', initialTab = 'memory', onClose }) {
  const [tab, setTab] = useState(initialTab);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    if (!uid) return;
    return subscribeToClients(uid, setClients);
  }, [uid]);

  const hasClients = clients.some(c => c.status === 'active');
  const isPro      = tier === 'pro';

  const tabs = [
    { id: 'memory',   label: 'Memory',    icon: Brain },
    { id: 'advisors', label: 'Advisors',  icon: UserCheck },
    ...(hasClients ? [{ id: 'clients',  label: 'Clients',   icon: Users }] : []),
    ...(isPro       ? [{ id: 'docs',    label: 'Documents', icon: FileText }] : []),
  ];

  return (
    <div style={{
      position: 'absolute', inset: 0,
      background: T.appBg,
      display: 'flex', flexDirection: 'column',
      zIndex: 10,
    }}>
      {/* Header */}
      <div style={{
        background: T.gradNavy, padding: '14px 16px',
        display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ color: '#fff', fontWeight: 800, fontSize: 15, letterSpacing: -0.3 }}>AI Settings</div>
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, marginTop: 1 }}>Memory, advisors & standing instructions</div>
        </div>
        <button
          onClick={onClose}
          style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, padding: '6px 8px', cursor: 'pointer', color: '#fff', display: 'flex' }}
        >
          <X size={18} />
        </button>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #E5E7EB', background: '#fff', flexShrink: 0 }}>
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              padding: '10px 0', border: 'none', borderBottom: tab === id ? `2px solid ${T.tealDeep}` : '2px solid transparent',
              background: 'none', cursor: 'pointer',
              fontSize: 12, fontWeight: 700,
              color: tab === id ? T.tealDeep : '#9CA3AF',
            }}
          >
            <Icon size={14} />
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'memory'   && <MemoryTab    uid={uid} />}
      {tab === 'advisors' && <AdvisorsTab  uid={uid} />}
      {tab === 'clients'  && <ClientsTab   uid={uid} />}
      {tab === 'docs'     && <DocumentsTab uid={uid} />}
    </div>
  );
}
